<template>
   <div class="lg:w-10/12 mx-auto mt-10">
      <Stepper :current-step="3" />
      <div class="lg:mb-16 flex flex-col gap-6 lg:flex-row lg:gap-10">
         <div class="w-full lg:w-8/12 order-last lg:order-first">
            <form @submit.prevent="submit" ref="formEl">
               <div class="space-y-4">
                  <!-- CUSTOMER INFORMATION -->
                  <div class="border border-slate-200 py-6 px-4 rounded-2xl space-y-8">
                     <Fieldset class="grid-cols-1 md:grid-cols-2 md:gap-6 gap-4">
                        <template #legend>
                           <h3 class="text-lg font-semibold">{{ $t("Booking holder") }}</h3>
                        </template>
                        <Field :error="form.errors.first_name" v-slot="{ hasError, errorId }">
                           <Label for="first_name">{{ $t("First name") }}</Label>
                           <Input v-model="form.first_name" id="first_name" autocomplete="given-name" :has-error="hasError" :error-id="errorId" />
                        </Field>

                        <Field :error="form.errors.last_name" v-slot="{ hasError, errorId }">
                           <Label for="last_name">{{ $t("Last name") }}</Label>
                           <Input v-model="form.last_name" id="last_name" autocomplete="family-name" :has-error="hasError" :error-id="errorId" />
                        </Field>

                        <Field :error="form.errors.address" v-slot="{ hasError, errorId }" class="md:col-span-2">
                           <Label for="address">{{ $t("Address") }}</Label>
                           <Input v-model="form.address" id="address" autocomplete="street-address" :has-error="hasError" :error-id="errorId" />
                        </Field>

                        <Field :error="form.errors.postcode" v-slot="{ hasError, errorId }">
                           <Label for="postcode">{{ $t("Zip code") }}</Label>
                           <Input v-model="form.postcode" id="postcode" autocomplete="postal-code" :has-error="hasError" :error-id="errorId" />
                        </Field>

                        <Field :error="form.errors.city" v-slot="{ hasError, errorId }">
                           <Label for="city">{{ $t("City") }}</Label>
                           <Input v-model="form.city" id="city" autocomplete="address-level2" :has-error="hasError" :error-id="errorId" />
                        </Field>

                        <Field :error="form.errors.country_code" v-slot="{ hasError, errorId }">
                           <Label for="country_code">{{ $t("Country") }}</Label>
                           <MultiSelect v-model="form.country_code" id="country_code" :options="countries" :closeOnSelect="true" trackBy="name" valueProp="code" label="name" mode="single" :has-error="hasError" :error-id="errorId" />
                        </Field>

                        <Field :error="form.errors.email" v-slot="{ hasError, errorId }">
                           <Label for="email">{{ $t("E-mail") }}</Label>
                           <Input type="email" v-model="form.email" id="email" autocomplete="email" :has-error="hasError" :error-id="errorId" />
                        </Field>

                        <Field :error="form.errors.phone" v-slot="{ hasError, errorId }">
                           <Label for="phone">{{ $t("Phone") }}</Label>
                           <Input v-model="form.phone" id="phone" autocomplete="tel" :has-error="hasError" :error-id="errorId" />
                        </Field>
                     </Fieldset>

                     <PaymentMethod :form="form" :payment-methods="paymentMethods" />

                     <PersonsDetails :form="form" :guest-counts="guestCounts" />

                     <RiskInsurance />

                     <Fieldset>
                        <template #legend>
                           <h3 class="text-lg font-semibold">{{ $t("Notes") }}</h3>
                        </template>
                        <Field :error="form.errors.note" v-slot="{ hasError, errorId }" class="md:col-span-2">
                           <Textarea rows="3" v-model="form.note" :has-error="hasError" :error-id="errorId" />
                           <p class="text-sm text-slate-600">{{ $t("All stated wishes represent guest preferences only and are not subject to a confirmed reservation.") }}</p>
                        </Field>
                     </Fieldset>
                     <Fieldset>
                        <template #legend>
                           <h3 class="text-lg font-semibold">{{ $t("Newsletter") }}</h3>
                        </template>
                        <Field class="md:col-span-2 border border-slate-200 p-4 rounded-xl space-y-2">
                           <Checkbox v-model="form.newsletter_subscription">
                              <span>{{ $t("Subscribe to newsletter") }}</span>
                           </Checkbox>
                           <div class="mt-2 text-sm pl-8 text-slate-600">{{ $t("Newsletter promotion text") }}</div>
                        </Field>
                     </Fieldset>
                     <Fieldset>
                        <!-- TERMS -->
                        <Field :error="form.errors.terms" v-slot="{ hasError, errorId }" class="md:col-span-2">
                           <Checkbox v-model="form.terms" :has-error="hasError" :error-id="errorId">
                              <span v-html="$t('I agree and am familiar with the <a href=\'{link}\'>terms of business</a>.', { link: 'pogoji-poslovanja' })" />
                           </Checkbox>
                        </Field>
                     </Fieldset>
                  </div>

                  <Button type="submit" :loading="form.processing" class="btn-primary btn-xl w-full">
                     {{ $t("Send inquiry") }}
                  </Button>
               </div>
            </form>
         </div>

         <div class="lg:w-4/12 isolate">
            <div class="rounded-2xl border border-gray-200 shadow-custom-xl bg-white sticky top-4 z-10">
               <Summary />
            </div>
         </div>
      </div>
   </div>
</template>
<script setup>
import RiskInsurance from "./_partials/RiskInsurance.vue";
import PaymentMethod from "./_partials/PaymentMethod.vue";
import PersonsDetails from "./_partials/PersonsDetails.vue";
import { storeToRefs } from "pinia";
import { useForm } from "laravel-precognition-vue";
import { useCheckoutStore } from "@/stores/checkout.js";
import { useCartStore } from "@/stores/cart.js";
import { useToast } from "@/composables/toast/useToast.js";
import { useScrollToFirstError } from "@/composables/useScrollToFirstError";
import { useCraftNavigation } from "@/composables/useCraftNavigation";
import { useI18n } from "vue-i18n";

const { t } = useI18n();

const formEl = ref(null);
const { scroll } = useScrollToFirstError(formEl);

const toast = useToast();
const router = useRouter();
const { reloadTo } = useCraftNavigation();
const checkoutStore = useCheckoutStore();
const cartStore = useCartStore();
const { countries, paymentMethods } = storeToRefs(checkoutStore);

const normalizeGuestCount = (value) => Math.max(0, Number(value) || 0);

const cartProductItem = computed(() => {
   return cartStore.cartItems.find((item) => item.attributes?.modelType === "product");
});

const guestCounts = computed(() => {
   const guests = cartProductItem.value?.attributes?.guests ?? {};

   return {
      adults: normalizeGuestCount(guests.adults),
      children: normalizeGuestCount(guests.children) + normalizeGuestCount(guests.teens),
   };
});

// Form
const form = useForm("post", "/api/checkout", {
   first_name: "",
   last_name: "",
   address: "",
   city: "",
   postcode: "",
   email: "",
   phone: "",
   country_code: "SI",
   payment_method_id: null,
   persons: [],
   note: "",
   terms: 1,
   newsletter_subscription: 0,
});

await cartStore.loadCart();

await checkoutStore.loadCheckout();

const submit = async () => {
   try {
      const { data } = await form.submit();

      const routeName = data.requires_redirect ? "checkout.payment" : "checkout.success";

      const { href } = router.resolve({
         name: routeName,
         params: { order: data.orderId },
      });
      reloadTo(href);
   } catch (e) {
      await handleSubmitError();
   }
};

const handleSubmitError = async () => {
   toast.error(t("Form validation error"));

   await nextTick();
   scroll();
};
</script>
